<template>
  <main class="collections-page">
    <header class="collections-header">
      <div class="header-navigation">
        <router-link :to="{ name: 'ceremonyHighlightCollections' }" class="back-link">
          ← All collections
        </router-link>
      </div>
      <p>Highlights</p>
      <form v-if="renaming" class="rename-form" @submit.prevent="saveRename">
        <label class="sr-only" for="collection-title">Collection name</label>
        <input
          id="collection-title"
          ref="renameInput"
          v-model="renameTitle"
          maxlength="100"
          :disabled="renamePending"
          @keydown.esc.prevent="cancelRename"
        />
        <button type="button" :disabled="renamePending" @click="cancelRename">
          Cancel
        </button>
        <button type="submit" :disabled="renamePending || !renameTitle.trim()">
          {{ renamePending ? 'Saving…' : 'Save' }}
        </button>
      </form>
      <div v-else class="collection-title-row">
        <h2 ref="collectionHeading" tabindex="-1">
          {{ collection?.title || 'Your saved highlights' }}
        </h2>
        <button
          v-if="collection"
          type="button"
          class="rename-trigger"
          aria-label="Rename collection"
          title="Rename collection"
          @click="startRename"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M13.5 6.5 17.5 10.5M4 20l3.8-.8L19 8a2.1 2.1 0 0 0-3-3L4.8 16.2 4 20Z" />
          </svg>
        </button>
      </div>
      <span v-if="collection">
        {{ collection.item_count }} saved moment{{ collection.item_count === 1 ? '' : 's' }}
        from {{ collection.ceremony_count }} ceremon{{ collection.ceremony_count === 1 ? 'y' : 'ies' }}
      </span>
      <div v-if="managementError" class="management-error" role="alert">
        {{ managementError }}
      </div>
    </header>

    <section v-if="loading" class="collection-state" aria-live="polite">
      <div class="state-mark"></div>
      <p>Loading your highlights…</p>
    </section>

    <section v-else-if="loadError" class="collection-state error-state" role="alert">
      <h3>Your highlights could not be loaded</h3>
      <p>{{ loadError }}</p>
      <button type="button" @click="load">Try again</button>
    </section>

    <section v-else-if="!collection?.items?.length" class="collection-state empty-state">
      <span aria-hidden="true">✦</span>
      <h3>Your highlights will appear here</h3>
      <p>
        Save useful moments while searching ceremonies, then return here to review them.
      </p>
      <router-link to="/admin/ceremony-search">Explore ceremonies</router-link>
    </section>

    <template v-else>
      <section
        class="preparation-card"
        :class="`status-${collectionExportStatus.toLowerCase()}`"
        aria-live="polite"
      >
        <span class="preparation-mark" aria-hidden="true">
          <span v-if="exportJobActive" class="state-mark"></span>
          <span v-else-if="collectionExportStatus === 'COMPLETE'">✓</span>
          <span v-else-if="collectionExportStatus === 'PARTIAL'">!</span>
          <span v-else>↧</span>
        </span>
        <div class="preparation-copy">
          <p>Export collection</p>
          <h3>{{ exportHeading }}</h3>
          <span>{{ exportCopy }}</span>
        </div>
        <div class="preparation-actions">
          <button
            v-if="showPreparationButton"
            type="button"
            :disabled="mutationPending"
            @click="prepareAll"
          >
            {{ preparationButtonLabel }}
          </button>
          <small v-if="exportJobActive && queuedForNextJobCount">
            {{ queuedForNextJobCount }} new or changed
            clip{{ queuedForNextJobCount === 1 ? '' : 's' }} will be prepared next.
          </small>
          <small v-else-if="exportJobActive">You can leave this page. We’ll keep working.</small>
          <small v-else-if="collectionExportStatus === 'COMPLETE' || collectionExportStatus === 'PARTIAL'">
            Downloads stay available for seven days.
          </small>
        </div>
      </section>

      <p v-if="prepareError" class="prepare-error" role="alert">{{ prepareError }}</p>

      <section class="collection-workspace" aria-label="Saved highlights">
      <aside class="moment-panel">
        <div class="panel-heading">
          <div>
            <p>Your shortlist</p>
            <h3>Saved moments</h3>
          </div>
          <span>{{ collection.item_count }}</span>
        </div>

        <p v-if="actionError" class="action-error" role="alert">{{ actionError }}</p>
        <p class="collection-announcement" aria-live="polite">{{ announcement }}</p>

        <ol class="highlight-list">
          <li
            v-for="(item, index) in collection.items"
            :key="item.item_id"
            :class="{ selected: item.item_id === activeItem?.item_id }"
          >
            <button
              class="item-select"
              type="button"
              :aria-current="item.item_id === activeItem?.item_id ? 'true' : undefined"
              @click="selectItem(item)"
            >
              <span class="item-number">{{ index + 1 }}</span>
              <span class="item-copy">
                <strong>{{ item.label || 'Ceremony highlight' }}</strong>
                <span>{{ item.ceremony_label || 'Ceremony' }}</span>
                <small>
                  Starts at {{ formatDuration(item.start_seconds) }} ·
                  {{ Math.max(1, item.end_seconds - item.start_seconds) }} sec
                </small>
                <span
                  v-if="item.export_status === 'COMPLETE'"
                  class="item-export-status ready"
                >Ready to download</span>
                <span
                  v-else-if="['ERROR', 'CANCELED', 'EXPIRED'].includes(item.export_status)"
                  class="item-export-status error"
                >Needs preparing</span>
              </span>
            </button>
            <div class="item-actions">
              <details v-if="collection.items.length > 1" class="move-menu">
                <summary>Move</summary>
                <div>
                  <button
                    type="button"
                    :disabled="index === 0 || mutationPending"
                    @click="moveItem(index, 0, $event)"
                  >Move to top</button>
                  <button
                    type="button"
                    :disabled="index === 0 || mutationPending"
                    @click="moveItem(index, index - 1, $event)"
                  >Move up</button>
                  <button
                    type="button"
                    :disabled="index === collection.items.length - 1 || mutationPending"
                    @click="moveItem(index, index + 1, $event)"
                  >Move down</button>
                  <button
                    type="button"
                    :disabled="index === collection.items.length - 1 || mutationPending"
                    @click="moveItem(index, collection.items.length - 1, $event)"
                  >Move to bottom</button>
                </div>
              </details>
              <button
                ref="removeButtons"
                type="button"
                class="remove-button"
                :disabled="mutationPending"
                @click="remove(item)"
              >
                {{ removingItemId === item.item_id ? 'Removing…' : 'Remove' }}
              </button>
            </div>
          </li>
        </ol>
      </aside>

      <section class="review-panel" aria-label="Review saved moment">
        <div v-if="contextLoading" class="preview-state" aria-live="polite">
          <div class="state-mark"></div>
          <p>Opening ceremony video…</p>
        </div>

        <div v-else-if="contextError" class="preview-state error-state" role="alert">
          <h3>Video preview unavailable</h3>
          <p>{{ contextError }}</p>
          <button type="button" @click="loadCeremonyContext(activeItem)">Try again</button>
        </div>

        <template v-else>
          <ceremony-media-player
            ref="mediaPlayer"
            :active-video-url="activeVideoUrl"
            :ceremony-video-error="videoError"
            :video-options="videoOptions"
            readiness="ready"
            @loadeddata="onPlayerLoadedData"
          />

          <div class="review-details">
            <div>
              <p>Selected moment</p>
              <h3>{{ activeItem?.label || 'Ceremony highlight' }}</h3>
              <span class="ceremony-label">
                {{ activeItem?.ceremony_label || 'Ceremony' }}
              </span>
              <span v-if="activeItem?.summary" class="moment-summary">
                {{ activeItem.summary }}
              </span>
            </div>
            <div class="review-tools">
              <dl>
                <div>
                  <dt>Starts at</dt>
                  <dd>{{ formatDuration(activeItem?.start_seconds) }}</dd>
                </div>
                <div>
                  <dt>Duration</dt>
                  <dd>{{ Math.max(1, activeItem.end_seconds - activeItem.start_seconds) }} sec</dd>
                </div>
              </dl>
              <button
                v-if="activeItem?.export_status === 'COMPLETE' && activeItem?.export_id"
                type="button"
                class="download-button"
                :disabled="downloadPending"
                @click="downloadActiveClip"
              >
                {{ downloadPending ? 'Opening download…' : 'Download MP4' }}
              </button>
              <p
                v-else-if="['ERROR', 'CANCELED', 'EXPIRED'].includes(activeItem?.export_status)"
                class="clip-error"
              >
                {{ activeItem.export_error || 'This clip could not be prepared.' }}
              </p>
              <p v-if="downloadError" class="clip-error" role="alert">{{ downloadError }}</p>
            </div>
          </div>

          <ceremony-section-export
            ref="collectionTrimmer"
            mode="trim"
            :active-event="activeEvent"
            :ceremony-id="activeItem.ceremony_id"
            :export-states="{}"
            :format-duration="formatDuration"
            :active-video-url="activeVideoUrl"
            :video-options="videoOptions"
            :ceremony-video-duration="ceremonyVideoDuration"
            :cut-times="cutTimes"
            :saving="trimSaving"
            :save-error="trimSaveError"
            :disabled="mutationPending"
            @modal-open="pausePlayer"
            @save-trim="saveTrim"
          />
        </template>
      </section>
      </section>
    </template>
  </main>
</template>

<script>
import 'video.js/dist/video-js.css'
import { trackAdminEvent } from '../../admin/activity.js'
import {
  getHighlightCollection,
  prepareHighlightCollection,
  removeHighlightCollectionItem,
  updateHighlightCollection,
  updateHighlightCollectionItem,
} from '../../api/highlightCollections.js'
import { getCeremonyContext, getSectionExport } from '../../api/sectionExports.js'
import CeremonyMediaPlayer from './search/CeremonyMediaPlayer.vue'
import CeremonySectionExport from './search/CeremonySectionExport.vue'

export default {
  name: 'CeremonyHighlightCollections',
  components: {
    CeremonyMediaPlayer,
    CeremonySectionExport,
  },
  data() {
    return {
      collection: null,
      loading: true,
      loadError: '',
      actionError: '',
      announcement: '',
      selectedItemId: '',
      removingItemId: '',
      reordering: false,
      contextLoading: false,
      contextError: '',
      contextRequestId: 0,
      ceremonyContext: {},
      ceremonyContexts: {},
      activeContextCeremonyId: '',
      ceremonyVideoDuration: 0,
      trimSaving: false,
      trimSaveError: '',
      preparePending: false,
      prepareError: '',
      exportPollTimer: null,
      downloadPending: false,
      downloadError: '',
      previewTimeHandler: null,
      previewPlayHandler: null,
      renaming: false,
      renameTitle: '',
      renamePending: false,
      managementError: '',
    }
  },
  computed: {
    activeItem() {
      return this.collection?.items?.find(item => item.item_id === this.selectedItemId)
        || this.collection?.items?.[0]
        || null
    },
    activeEvent() {
      if (!this.activeItem) return {}
      return {
        id: this.activeItem.source_id,
        ceremonyId: this.activeItem.ceremony_id,
        type: this.activeItem.source_type,
        title: this.activeItem.label || 'Ceremony highlight',
        description: this.activeItem.summary || '',
        timestamp: this.activeItem.start_seconds,
        windowStartSeconds: this.activeItem.start_seconds,
        windowEndSeconds: this.activeItem.end_seconds,
        cluster_start_sec: this.activeItem.start_seconds,
        cluster_end_sec: this.activeItem.end_seconds,
      }
    },
    activeVideoUrl() {
      return String(this.ceremonyContext?.preview?.url || '')
    },
    activeVideoType() {
      const type = String(this.ceremonyContext?.preview?.type || '')
      if (type) return type
      return this.activeVideoUrl.toLowerCase().split('?')[0].endsWith('.mp4')
        ? 'video/mp4'
        : 'application/x-mpegURL'
    },
    videoError() {
      return this.activeVideoUrl ? '' : 'No indexed ceremony video is available.'
    },
    cutTimes() {
      return Array.isArray(this.ceremonyContext?.cut_times)
        ? this.ceremonyContext.cut_times
        : []
    },
    videoOptions() {
      return {
        autoplay: false,
        controls: true,
        preload: 'auto',
        fluid: false,
        responsive: false,
        width: 1920,
        height: 1080,
        sources: this.activeVideoUrl
          ? [{ type: this.activeVideoType, src: this.activeVideoUrl }]
          : [],
      }
    },
    mutationPending() {
      return Boolean(
        this.reordering
        || this.removingItemId
        || this.trimSaving
        || this.preparePending
        || this.renamePending
      )
    },
    exportJobStatus() {
      return String(this.collection?.export_job?.status || '').toUpperCase()
    },
    exportJobActive() {
      return ['QUEUED', 'RUNNING'].includes(this.exportJobStatus)
    },
    collectionExportStatus() {
      if (this.exportJobActive) return this.exportJobStatus
      const total = Number(this.collection?.item_count || 0)
      if (total && this.readyItemCount === total) return 'COMPLETE'
      if (this.readyItemCount) return 'PARTIAL'
      if (this.failedItemCount || this.exportJobStatus === 'ERROR') return 'ERROR'
      return 'IDLE'
    },
    exportHeading() {
      const ready = this.readyItemCount
      const total = Number(this.collection?.item_count || 0)
      if (this.exportJobActive) {
        const prepared = Number(this.collection?.export_job?.prepared_count || 0)
        const jobTotal = Number(this.collection?.export_job?.item_count || 0)
        return jobTotal
          ? `Preparing ${prepared} of ${jobTotal} clip${jobTotal === 1 ? '' : 's'}`
          : 'Preparing your clips'
      }
      if (this.collectionExportStatus === 'COMPLETE') {
        const count = ready || total
        return `${count} clip${count === 1 ? ' is' : 's are'} ready`
      }
      if (this.collectionExportStatus === 'PARTIAL') {
        return `${ready} of ${total} clips ${ready === 1 ? 'is' : 'are'} ready`
      }
      if (this.collectionExportStatus === 'ERROR') {
        return `${this.needsPreparationCount} clip${this.needsPreparationCount === 1 ? '' : 's'} need preparing`
      }
      return 'Turn this shortlist into downloadable clips'
    },
    exportCopy() {
      if (this.exportJobActive) {
        return 'Keep adding or refining highlights while these clips are prepared.'
      }
      if (this.collectionExportStatus === 'COMPLETE') {
        return 'Choose any saved moment below to download its clip.'
      }
      if (this.collectionExportStatus === 'PARTIAL') {
        return 'Ready clips stay available while the remaining clips are prepared.'
      }
      if (this.collectionExportStatus === 'ERROR') {
        return 'Your saved moments and trims are safe. Try the remaining clips again.'
      }
      return 'Review the order and trims, then prepare every saved moment in one go.'
    },
    preparationButtonLabel() {
      if (this.preparePending) return 'Queuing clips…'
      const count = this.needsPreparationCount
      const suffix = count === 1 ? 'clip' : 'clips'
      if (['PARTIAL', 'ERROR'].includes(this.collectionExportStatus)) {
        return `Prepare ${count} remaining ${suffix}`
      }
      return `Prepare ${count} ${suffix}`
    },
    readyItemCount() {
      return (this.collection?.items || []).filter(
        item => item.export_status === 'COMPLETE' && item.export_id
      ).length
    },
    failedItemCount() {
      return (this.collection?.items || []).filter(
        item => ['ERROR', 'CANCELED', 'EXPIRED'].includes(item.export_status)
      ).length
    },
    needsPreparationCount() {
      const supplied = Number(this.collection?.needs_preparation_count)
      if (Number.isFinite(supplied)) return Math.max(0, supplied)
      return Math.max(0, Number(this.collection?.item_count || 0) - this.readyItemCount)
    },
    queuedForNextJobCount() {
      if (!this.exportJobActive) return 0
      const jobSize = Number(this.collection?.export_job?.item_count || 0)
      return Math.max(0, this.needsPreparationCount - jobSize)
    },
    showPreparationButton() {
      return Boolean(!this.exportJobActive && this.needsPreparationCount)
    },
  },
  created() {
    this.load()
  },
  beforeDestroy() {
    this.clearPreviewBounds()
    this.clearExportPoll()
  },
  methods: {
    track(activityType, metadata = {}) {
      return trackAdminEvent(activityType, {
        toolKey: 'ceremony_intelligence',
        metadata,
      })
    },
    startRename() {
      if (!this.collection || this.renamePending) return
      this.managementError = ''
      this.renameTitle = this.collection.title || ''
      this.renaming = true
      this.$nextTick(() => {
        this.$refs.renameInput?.focus()
        this.$refs.renameInput?.select()
      })
    },
    cancelRename() {
      if (this.renamePending) return
      this.renaming = false
      this.renameTitle = ''
      this.managementError = ''
      this.$nextTick(() => this.$refs.collectionHeading?.focus())
    },
    async saveRename() {
      const title = String(this.renameTitle || '').trim()
      if (!this.collection?.collection_id || !title || this.renamePending) return
      if (title === this.collection.title) {
        this.cancelRename()
        return
      }
      this.renamePending = true
      this.managementError = ''
      try {
        const response = await updateHighlightCollection(
          this.collection.collection_id,
          { title }
        )
        this.collection = response.data
        this.renaming = false
        this.renameTitle = ''
        this.announcement = `Collection renamed to “${this.collection.title}”.`
        this.track('highlight_collection_renamed', {
          collectionId: this.collection.collection_id,
        })
        this.$nextTick(() => this.$refs.collectionHeading?.focus())
      } catch (error) {
        if (error?.response?.status === 409) await this.reloadCollection()
        this.managementError = (
          error?.response?.data?.error || 'The collection could not be renamed.'
        )
      } finally {
        this.renamePending = false
      }
    },
    async load() {
      this.loading = true
      this.loadError = ''
      try {
        const collectionId = String(this.$route.params.collectionId || '')
        if (!collectionId) {
          this.$router.replace({ name: 'ceremonyHighlightCollections' })
          return
        }
        const response = await getHighlightCollection(collectionId)
        this.collection = response.data
        this.scheduleExportPoll()
        this.track('highlight_collection_opened', {
          collectionId,
          itemCount: this.collection.item_count,
          ceremonyCount: this.collection.ceremony_count,
        })
        if (this.collection.items?.length) this.selectItem(this.collection.items[0])
      } catch (error) {
        this.loadError = error?.response?.data?.error || 'Please try again.'
      } finally {
        this.loading = false
      }
    },
    async selectItem(item) {
      if (!item?.item_id) return
      this.clearPreviewBounds()
      this.selectedItemId = item.item_id
      this.trimSaveError = ''
      this.downloadError = ''
      await this.loadCeremonyContext(item)
    },
    async loadCeremonyContext(item) {
      if (!item?.ceremony_id) return
      const requestId = ++this.contextRequestId
      this.contextLoading = true
      this.contextError = ''
      if (item.ceremony_id !== this.activeContextCeremonyId) {
        this.ceremonyVideoDuration = 0
      }
      const cachedContext = this.ceremonyContexts[item.ceremony_id]
      if (cachedContext) {
        this.ceremonyContext = cachedContext
        this.activeContextCeremonyId = item.ceremony_id
        this.contextLoading = false
        await this.$nextTick()
        this.seekActiveItem()
        this.bindPreviewBounds()
        return
      }
      this.ceremonyContext = {}
      try {
        const response = await getCeremonyContext(item.ceremony_id)
        if (requestId !== this.contextRequestId || item.item_id !== this.selectedItemId) return
        this.ceremonyContext = response.data || {}
        this.activeContextCeremonyId = item.ceremony_id
        this.$set(this.ceremonyContexts, item.ceremony_id, this.ceremonyContext)
        await this.$nextTick()
        this.seekActiveItem()
        this.bindPreviewBounds()
      } catch (error) {
        if (requestId !== this.contextRequestId) return
        this.contextError = (
          error?.response?.data?.error
          || 'The indexed ceremony video could not be loaded.'
        )
      } finally {
        if (requestId === this.contextRequestId) this.contextLoading = false
      }
    },
    onPlayerLoadedData() {
      const duration = this.$refs.mediaPlayer?.player?.duration?.()
      if (Number.isFinite(duration) && duration > 0) this.ceremonyVideoDuration = duration
      this.seekActiveItem()
      this.bindPreviewBounds()
    },
    seekActiveItem() {
      const player = this.$refs.mediaPlayer?.player
      if (!player || !this.activeItem) return
      player.currentTime(Number(this.activeItem.start_seconds) || 0)
    },
    pausePlayer() {
      this.$refs.mediaPlayer?.player?.pause?.()
    },
    clearPreviewBounds() {
      const player = this.$refs.mediaPlayer?.player
      if (player && this.previewTimeHandler) player.off('timeupdate', this.previewTimeHandler)
      if (player && this.previewPlayHandler) player.off('play', this.previewPlayHandler)
      this.previewTimeHandler = null
      this.previewPlayHandler = null
    },
    bindPreviewBounds() {
      this.clearPreviewBounds()
      const player = this.$refs.mediaPlayer?.player
      if (!player || !this.activeItem) return
      const start = Number(this.activeItem.start_seconds) || 0
      const end = Number(this.activeItem.end_seconds) || start + 1
      this.previewPlayHandler = () => {
        const current = Number(player.currentTime()) || 0
        if (current < start || current >= end - 0.1) player.currentTime(start)
      }
      this.previewTimeHandler = () => {
        if ((Number(player.currentTime()) || 0) < end) return
        player.pause()
        player.currentTime(end)
      }
      player.on('play', this.previewPlayHandler)
      player.on('timeupdate', this.previewTimeHandler)
    },
    async saveTrim({ startSeconds, endSeconds }) {
      if (!this.activeItem || this.mutationPending) return
      this.trimSaving = true
      this.trimSaveError = ''
      try {
        const response = await updateHighlightCollectionItem(
          this.collection.collection_id,
          this.activeItem.item_id,
          { start_seconds: startSeconds, end_seconds: endSeconds }
        )
        this.collection = response.data.collection
        this.selectedItemId = response.data.item.item_id
        this.track('highlight_trim_saved', {
          collectionId: this.collection.collection_id,
          ceremonyId: response.data.item.ceremony_id,
          sourceType: response.data.item.source_type,
          durationSeconds: endSeconds - startSeconds,
        })
        this.announcement = 'Clip timing saved.'
        this.$refs.collectionTrimmer?.closeModal()
        await this.$nextTick()
        this.seekActiveItem()
        this.bindPreviewBounds()
      } catch (error) {
        this.trimSaveError = (
          error?.response?.data?.error || 'The clip timing could not be saved.'
        )
      } finally {
        this.trimSaving = false
      }
    },
    async moveItem(index, targetIndex, event) {
      if (this.mutationPending) return
      if (targetIndex < 0 || targetIndex >= this.collection.items.length) return
      if (targetIndex === index) return
      const previousItems = this.collection.items.slice()
      const items = previousItems.slice()
      const [moved] = items.splice(index, 1)
      items.splice(targetIndex, 0, moved)
      this.collection = { ...this.collection, items }
      this.reordering = true
      this.actionError = ''
      try {
        const response = await updateHighlightCollection(
          this.collection.collection_id,
          { item_ids: items.map(item => item.item_id) }
        )
        this.collection = response.data
        this.track('highlight_reordered', {
          collectionId: this.collection.collection_id,
          itemCount: this.collection.item_count,
        })
        this.announcement = `“${moved.label || 'Ceremony highlight'}” moved.`
        event?.currentTarget?.closest('details')?.removeAttribute('open')
      } catch (error) {
        const reloaded = error?.response?.status === 409
          ? await this.reloadCollection()
          : false
        if (!reloaded) this.collection = { ...this.collection, items: previousItems }
        this.actionError = error?.response?.data?.error || 'The new order could not be saved.'
      } finally {
        this.reordering = false
      }
    },
    async remove(item) {
      if (!item?.item_id || !this.collection?.collection_id || this.mutationPending) return
      const itemIndex = this.collection.items.findIndex(saved => saved.item_id === item.item_id)
      this.removingItemId = item.item_id
      this.actionError = ''
      this.announcement = ''
      try {
        const response = await removeHighlightCollectionItem(
          this.collection.collection_id,
          item.item_id
        )
        this.collection = response.data
        this.track('highlight_removed', {
          collectionId: this.collection.collection_id,
          ceremonyId: item.ceremony_id,
          sourceType: item.source_type,
          itemCount: this.collection.item_count,
        })
        this.announcement = `“${item.label || 'Ceremony highlight'}” removed from highlights.`
        if (item.item_id === this.selectedItemId && this.collection.items.length) {
          await this.selectItem(
            this.collection.items[Math.min(itemIndex, this.collection.items.length - 1)]
          )
        }
        this.$nextTick(() => {
          const buttons = Array.isArray(this.$refs.removeButtons)
            ? this.$refs.removeButtons
            : [this.$refs.removeButtons].filter(Boolean)
          const nextButton = buttons[Math.min(itemIndex, buttons.length - 1)]
          if (nextButton) nextButton.focus()
          else this.$refs.collectionHeading?.focus()
        })
      } catch (error) {
        if (error?.response?.status === 409) await this.reloadCollection()
        this.actionError = (
          error?.response?.data?.error
          || `“${item.label || 'This highlight'}” couldn't be removed. Try again.`
        )
      } finally {
        this.removingItemId = ''
      }
    },
    async reloadCollection() {
      try {
        const response = await getHighlightCollection(this.collection.collection_id)
        this.collection = response.data
        if (!this.collection.items.some(item => item.item_id === this.selectedItemId)) {
          this.selectedItemId = this.collection.items[0]?.item_id || ''
        }
        return true
      } catch (_) {
        return false
      }
    },
    async prepareAll() {
      if (!this.collection?.collection_id || this.mutationPending) return
      this.preparePending = true
      this.prepareError = ''
      try {
        const response = await prepareHighlightCollection(this.collection.collection_id)
        this.collection = response.data
        this.track('highlight_prepare_requested', {
          collectionId: this.collection.collection_id,
          itemCount: this.collection.item_count,
          ceremonyCount: this.collection.ceremony_count,
        })
        this.scheduleExportPoll()
      } catch (error) {
        this.prepareError = (
          error?.response?.data?.error
          || 'Your clips could not be queued. Please try again.'
        )
      } finally {
        this.preparePending = false
      }
    },
    clearExportPoll() {
      if (this.exportPollTimer) clearTimeout(this.exportPollTimer)
      this.exportPollTimer = null
    },
    scheduleExportPoll() {
      this.clearExportPoll()
      if (!this.exportJobActive) return
      this.exportPollTimer = setTimeout(this.pollExportJob, 10000)
    },
    async pollExportJob() {
      this.exportPollTimer = null
      if (!this.collection?.collection_id) return
      const previousStatus = this.exportJobStatus
      try {
        const response = await getHighlightCollection(this.collection.collection_id)
        this.collection = response.data
        if (
          ['QUEUED', 'RUNNING'].includes(previousStatus)
          && ['COMPLETE', 'PARTIAL', 'ERROR'].includes(this.exportJobStatus)
        ) {
          this.announcement = this.exportHeading
        }
      } catch (_) {
        // The next page visit can resume from the persisted export job.
      }
      this.scheduleExportPoll()
    },
    async downloadActiveClip() {
      if (!this.activeItem?.export_id || this.downloadPending) return
      this.downloadPending = true
      this.downloadError = ''
      try {
        const result = await getSectionExport(this.activeItem.export_id)
        const downloadUrl = result.data?.download_url
        if (!downloadUrl) throw new Error('The clip download is not ready yet.')
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = ''
        link.rel = 'noopener'
        document.body.appendChild(link)
        link.click()
        link.remove()
        this.track('highlight_downloaded', {
          collectionId: this.collection.collection_id,
          ceremonyId: this.activeItem.ceremony_id,
          sourceType: this.activeItem.source_type,
        })
      } catch (error) {
        if (error?.response?.status === 404 && this.activeItem) {
          this.$set(this.activeItem, 'export_status', 'EXPIRED')
        }
        this.downloadError = (
          error?.response?.status === 404
            ? 'This download has expired. Prepare the collection again.'
            : error?.response?.data?.error
              || error?.message
              || 'The clip download could not be opened.'
        )
      } finally {
        this.downloadPending = false
      }
    },
    formatDuration(totalSeconds) {
      const safe = Math.max(0, Math.round(Number(totalSeconds) || 0))
      const hours = Math.floor(safe / 3600)
      const minutes = Math.floor((safe % 3600) / 60)
      const seconds = safe % 60
      if (hours) {
        return [hours, minutes, seconds]
          .map((part, index) => String(part).padStart(index ? 2 : 1, '0'))
          .join(':')
      }
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    },
  },
}
</script>

<style scoped>
.collections-page {
  max-width: 1180px;
  margin: 0 auto;
  padding: 12px 24px 56px;
}

.collections-header {
  max-width: 760px;
  margin-bottom: 30px;
}

.header-navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.back-link {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  color: #5f6879;
  font-size: 0.78rem;
  font-weight: 650;
  text-decoration: none;
}

.rename-trigger {
  display: inline-flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid #dfe2e9;
  border-radius: 50%;
  background: #fff;
  color: #5f6879;
  cursor: pointer;
  transition: border-color 150ms ease, background 150ms ease, color 150ms ease;
}

.rename-trigger:hover,
.rename-trigger:focus-visible {
  border-color: #c4b5fd;
  background: #f7f4ff;
  color: #6d28d9;
}

.rename-trigger svg {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}

.collection-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.collections-header p,
.panel-heading p,
.review-details p {
  margin: 18px 0 5px;
  color: #7c3aed;
  font-size: 0.7rem;
  font-weight: 750;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.collections-header h2 {
  margin: 0;
  color: #171a25;
  font-size: clamp(1.8rem, 4vw, 2.65rem);
  letter-spacing: -0.04em;
  line-height: 1.08;
}

.collections-header h2:focus {
  outline: none;
}

.rename-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 8px;
  max-width: 680px;
}

.rename-form input {
  min-width: 0;
  min-height: 48px;
  padding: 0 14px;
  border: 1px solid #a78bfa;
  border-radius: 11px;
  background: #fff;
  color: #171a25;
  font: inherit;
  font-size: 1.05rem;
  font-weight: 720;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
}

.rename-form button {
  min-height: 44px;
  padding: 0 15px;
  border: 1px solid #d8dbe3;
  border-radius: 10px;
  background: #fff;
  color: #353b4a;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 720;
  cursor: pointer;
}

.rename-form button[type='submit'] {
  border-color: #6d28d9;
  background: #6d28d9;
  color: #fff;
}

.rename-form button:disabled {
  opacity: 0.55;
  cursor: default;
}

.management-error {
  margin-top: 12px;
  padding: 10px 13px;
  border-radius: 10px;
  background: #fff3f1;
  color: #b42318;
  font-size: 0.76rem;
  line-height: 1.45;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.collections-header > span {
  display: block;
  margin-top: 10px;
  color: #707787;
  font-size: 0.9rem;
}

.preparation-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
  padding: 18px 20px;
  border: 1px solid #ddd6fe;
  border-radius: 18px;
  background: linear-gradient(135deg, #faf8ff 0%, #f4f1ff 100%);
  box-shadow: 0 10px 28px rgba(82, 53, 132, 0.07);
}

.preparation-card.status-complete {
  border-color: #c8ead8;
  background: linear-gradient(135deg, #f5fcf8 0%, #edf9f3 100%);
}

.preparation-card.status-partial,
.preparation-card.status-error {
  border-color: #f1d8bd;
  background: linear-gradient(135deg, #fffaf4 0%, #fff5e9 100%);
}

.preparation-mark {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 13px;
  background: #fff;
  color: #6d28d9;
  font-size: 1.05rem;
  font-weight: 800;
  box-shadow: 0 4px 14px rgba(40, 25, 80, 0.09);
}

.preparation-card.status-complete .preparation-mark {
  color: #067647;
}

.preparation-card.status-partial .preparation-mark,
.preparation-card.status-error .preparation-mark {
  color: #b54708;
}

.preparation-copy p {
  margin: 0 0 4px;
  color: #7c3aed;
  font-size: 0.65rem;
  font-weight: 750;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.preparation-copy h3 {
  margin: 0;
  color: #252335;
  font-size: 0.96rem;
}

.preparation-copy > span,
.preparation-actions small {
  display: block;
  margin-top: 4px;
  color: #726d7f;
  font-size: 0.74rem;
  line-height: 1.45;
}

.preparation-actions {
  display: grid;
  justify-items: end;
  gap: 3px;
}

.preparation-actions button,
.download-button {
  min-height: 44px;
  padding: 0 18px;
  border: 0;
  border-radius: 11px;
  background: #6d28d9;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 720;
  cursor: pointer;
}

.preparation-actions button:disabled,
.download-button:disabled {
  opacity: 0.58;
  cursor: default;
}

.prepare-error {
  margin: -8px 0 18px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #fff3f1;
  color: #b42318;
  font-size: 0.76rem;
}

.collection-workspace {
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr);
  align-items: start;
  gap: 24px;
}

.moment-panel,
.review-panel {
  border: 1px solid #e3e6ed;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(24, 31, 47, 0.05);
}

.moment-panel {
  overflow: hidden;
}

.panel-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 18px 14px;
}

.panel-heading p {
  margin-top: 0;
}

.panel-heading h3,
.review-details h3 {
  margin: 0;
  color: #222735;
  font-size: 1rem;
}

.panel-heading > span {
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  border-radius: 50%;
  background: #f3efff;
  color: #6d28d9;
  font-size: 0.76rem;
  font-weight: 750;
}

.highlight-list {
  display: grid;
  max-height: 640px;
  gap: 1px;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  background: #eef0f4;
  list-style: none;
}

.highlight-list li {
  background: #fff;
  transition: background 0.15s ease;
}

.highlight-list li.selected {
  background: #faf8ff;
  box-shadow: inset 3px 0 #7c3aed;
}

.item-select {
  display: grid;
  width: 100%;
  grid-template-columns: 36px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  padding: 14px 16px 8px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.item-number {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 10px;
  background: #f5f3ff;
  color: #6d28d9;
  font-size: 0.75rem;
  font-weight: 750;
}

.item-copy {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.item-copy strong {
  overflow: hidden;
  color: #222735;
  font-size: 0.84rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-copy span,
.item-copy small,
.review-details > div > span {
  color: #777f8f;
  font-size: 0.72rem;
  line-height: 1.45;
}

.item-copy .item-export-status {
  width: fit-content;
  margin-top: 3px;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 0.62rem;
  font-weight: 720;
}

.item-export-status.ready {
  background: #eaf8f0;
  color: #067647;
}

.item-export-status.error {
  background: #fff1e8;
  color: #b54708;
}

.review-details .ceremony-label,
.review-details .moment-summary {
  display: block;
}

.review-details .ceremony-label {
  margin-top: 5px;
}

.review-details .moment-summary {
  margin-top: 3px;
  color: #606879;
}

.item-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding: 0 12px 8px 64px;
}

.item-actions button,
.move-menu summary {
  min-height: 44px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #667085;
  font-size: 0.76rem;
  font-weight: 650;
  cursor: pointer;
}

.item-actions button:hover:not(:disabled),
.move-menu summary:hover {
  background: #f4f1fb;
  color: #5b21b6;
}

.item-actions button:disabled {
  opacity: 0.3;
  cursor: default;
}

.item-actions .remove-button {
  min-width: 66px;
}

.move-menu {
  order: 1;
}

.move-menu summary {
  display: inline-flex;
  align-items: center;
  padding: 0 10px;
  list-style: none;
}

.move-menu summary::-webkit-details-marker {
  display: none;
}

.move-menu[open] summary {
  background: #f4f1fb;
  color: #5b21b6;
}

.move-menu[open] {
  flex: 1 0 100%;
  order: 2;
}

.move-menu > div {
  display: grid;
  width: 100%;
  margin-top: 2px;
  padding: 5px;
  border: 1px solid #dedfea;
  border-radius: 11px;
  background: #fff;
  box-shadow: 0 12px 30px rgba(24, 31, 47, 0.16);
}

.move-menu > div button {
  width: 100%;
  padding: 0 10px;
  text-align: left;
}

.item-actions .remove-button {
  order: 1;
}

.review-panel {
  min-width: 0;
  padding: 18px;
}

.review-panel :deep(.video-stage) {
  box-shadow: none;
}

.review-details {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 20px;
  padding: 18px 2px;
  border-bottom: 1px solid #e8eaf0;
}

.review-details p {
  margin-top: 0;
}

.review-details dl {
  display: grid;
  grid-template-columns: repeat(2, minmax(76px, 1fr));
  gap: 8px;
  margin: 0;
}

.review-tools {
  display: grid;
  justify-items: end;
  gap: 9px;
}

.review-tools .download-button {
  width: 100%;
}

.clip-error {
  max-width: 210px;
  margin: 0;
  color: #b42318;
  font-size: 0.7rem;
  line-height: 1.4;
  text-align: right;
}

.review-details dl div {
  padding: 10px 12px;
  border-radius: 11px;
  background: #f7f7fa;
}

.review-details dt {
  color: #8a91a0;
  font-size: 0.62rem;
}

.review-details dd {
  margin: 4px 0 0;
  color: #232938;
  font-size: 0.84rem;
  font-weight: 720;
}

.review-panel :deep(.section-export) {
  padding-top: 18px;
}

.preview-state,
.collection-state {
  display: grid;
  min-height: 340px;
  align-content: center;
  justify-items: center;
  padding: 32px;
  text-align: center;
}

.collection-state {
  border: 1px solid #e4e7ed;
  border-radius: 18px;
  background: #fff;
}

.preview-state h3,
.collection-state h3,
.preview-state p,
.collection-state p {
  margin: 7px 0;
}

.preview-state p,
.collection-state p {
  color: #697184;
  font-size: 0.78rem;
  line-height: 1.55;
}

.collection-state > span {
  color: #8b5cf6;
  font-size: 1.5rem;
}

.preview-state button,
.collection-state a,
.collection-state button {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  margin-top: 10px;
  padding: 0 16px;
  border: 0;
  border-radius: 10px;
  background: #6d28d9;
  color: #fff;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
}

.action-error {
  margin: 0;
  padding: 10px 16px;
  background: #fff3f1;
  color: #b42318;
  font-size: 0.76rem;
}

.collection-announcement {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

.state-mark {
  width: 28px;
  height: 28px;
  border: 3px solid #e5ddfa;
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .state-mark { animation: none; }
  .highlight-list li { transition: none; }
}

@media (max-width: 899px) {
  .collection-workspace {
    grid-template-columns: 1fr;
  }

  .highlight-list {
    max-height: 360px;
  }
}

@media (max-width: 640px) {
  .collections-page {
    padding: 6px 16px calc(40px + env(safe-area-inset-bottom));
  }

  .review-panel {
    padding: 10px;
  }

  .rename-form {
    grid-template-columns: 1fr 1fr;
  }

  .rename-form input {
    grid-column: 1 / -1;
  }

  .collection-title-row {
    align-items: flex-start;
    gap: 8px;
  }

  .rename-trigger {
    width: 38px;
    height: 38px;
    flex-basis: 38px;
  }

  .preparation-card {
    grid-template-columns: auto minmax(0, 1fr);
    align-items: start;
    padding: 16px;
  }

  .preparation-actions {
    grid-column: 1 / -1;
    width: 100%;
    justify-items: stretch;
  }

  .preparation-actions small {
    text-align: center;
  }

  .review-details {
    grid-template-columns: 1fr;
    padding: 16px 4px;
  }

  .review-details dl {
    width: 100%;
  }

  .review-tools {
    width: 100%;
    justify-items: stretch;
  }

  .clip-error {
    max-width: none;
    text-align: left;
  }
}
</style>
